import React, { FC, useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  IconButton,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { Chip, TextField } from '@mui/material';
import { format, parseISO } from 'date-fns';
import useService from '../../hooks/service.hook';
import EquipmentSharingService from '../../services/equipment-sharing.service';
import { useSnackbar } from 'notistack';
import { isEmailInvalid } from '../../helpers/general.helpers';
import { UserAvatar } from '../user-avatar/user-avatar.component';

interface PendingInvitation {
  id: number;
  email: string;
  firstname?: string;
  lastname?: string;
  created_at: string;
  status: string;
  invitation_type?: 'owner_to_manager' | 'owner_transfer' | 'manager_to_athlete';
  athlete_user_id?: number | null;
}

export interface RequestAthleteManagementDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onInvitesSent?: () => void;
}

const RequestAthleteManagementDialog: FC<RequestAthleteManagementDialogProps> = ({
  isOpen,
  onClose,
  onInvitesSent,
}) => {
  const equipmentSharingService = useService(EquipmentSharingService);
  const { enqueueSnackbar } = useSnackbar();

  const [pendingInvitations, setPendingInvitations] = useState<PendingInvitation[]>([]);
  const [invitationsLoading, setInvitationsLoading] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteEmailError, setInviteEmailError] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);

  const athleteInvitations = useMemo(
    () => pendingInvitations.filter((i) => i.invitation_type === 'manager_to_athlete'),
    [pendingInvitations]
  );

  useEffect(() => {
    if (isOpen) {
      fetchPendingInvitations();
    }
  }, [isOpen]);

  const fetchPendingInvitations = () => {
    setInvitationsLoading(true);
    equipmentSharingService
      .getSentEquipmentInvitations()
      .then((res) => {
        setPendingInvitations(res.data.data || []);
        setInvitationsLoading(false);
      })
      .catch((err) => {
        setInvitationsLoading(false);
        let errorMessage = 'Failed to fetch pending invitations. Please try again later';
        if (err?.response?.status === 403) {
          errorMessage = 'You do not have permission to view invitations.';
        }
        enqueueSnackbar(errorMessage, {
          autoHideDuration: 3000,
          variant: 'error',
          anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
        });
      });
  };

  const handleCancelInvitation = (invitationId: number, email: string) => {
    if (window.confirm(`Are you sure you want to cancel the invitation for ${email}?`)) {
      equipmentSharingService
        .cancelEquipmentInvitation(invitationId)
        .then(() => {
          enqueueSnackbar('Successfully canceled invitation', {
            autoHideDuration: 3000,
            variant: 'success',
            anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
          });
          fetchPendingInvitations();
          onInvitesSent?.();
        })
        .catch((err) => {
          let errorMessage = 'Failed to cancel invitation. Please try again later';
          if (err?.response?.status === 400) {
            errorMessage = 'This invitation is no longer pending.';
          } else if (err?.response?.status === 403) {
            errorMessage = 'You do not have permission to cancel this invitation.';
          } else if (err?.response?.status === 404) {
            errorMessage = 'Invitation not found.';
          }
          enqueueSnackbar(errorMessage, {
            autoHideDuration: 3000,
            variant: 'error',
            anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
          });
        });
    }
  };

  const handleInviteSubmit = () => {
    setInviteEmailError('');
    if (isEmailInvalid(inviteEmail)) {
      setInviteEmailError('This email is invalid. Please use a valid email');
      return;
    }
    setInviteLoading(true);
    equipmentSharingService
      .sendEquipmentInvitation('manager_to_athlete', inviteEmail)
      .then(() => {
        enqueueSnackbar('Successfully sent request to manage athlete equipment', {
          autoHideDuration: 3000,
          variant: 'success',
          anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
        });
        setInviteEmail('');
        fetchPendingInvitations();
        onInvitesSent?.();
      })
      .catch((err) => {
        let errorMessage = 'Failed to send request. Please try again later';
        if (err?.response?.status === 400) {
          errorMessage = err?.response?.data?.message || 'Invalid request. Please check the email address.';
        } else if (err?.response?.status === 403) {
          errorMessage = 'You do not have permission to send this request.';
        } else if (err?.response?.status === 404) {
          errorMessage = 'That email is not registered on CoeLab yet.';
        }
        enqueueSnackbar(errorMessage, {
          autoHideDuration: 3000,
          variant: 'error',
          anchorOrigin: { horizontal: 'center', vertical: 'bottom' },
        });
      })
      .finally(() => {
        setInviteLoading(false);
      });
  };

  const renderPendingInvitations = () => {
    if (invitationsLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', padding: 2 }}>
          <CircularProgress size={40} />
        </Box>
      );
    }

    if (athleteInvitations.length === 0) {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            padding: 4,
          }}
        >
          <MailOutlineIcon sx={{ fontSize: 60, color: 'grey' }} />
          <Typography color="text.secondary" sx={{ mt: 2 }}>
            No pending requests
          </Typography>
        </Box>
      );
    }

    return (
      <List>
        {athleteInvitations.map((invitation) => {
          const displayName =
            invitation.firstname && invitation.lastname
              ? `${invitation.firstname} ${invitation.lastname}`
              : invitation.email;
          return (
            <ListItem
              key={invitation.id}
              secondaryAction={
                <IconButton
                  edge="end"
                  aria-label="cancel invitation"
                  onClick={() => handleCancelInvitation(invitation.id, invitation.email)}
                  color="error"
                >
                  <DeleteIcon />
                </IconButton>
              }
            >
              <ListItemAvatar>
                <UserAvatar name={displayName} />
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography>{displayName}</Typography>
                    <Chip
                      label="Pending Request"
                      color="default"
                      size="small"
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  </Box>
                }
                secondary={`${invitation.email} • Sent: ${format(parseISO(invitation.created_at), 'MMM do, Y')}`}
              />
            </ListItem>
          );
        })}
      </List>
    );
  };

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Request Athlete Management</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2 }}>
              <TextField
                label="Athlete Email"
                value={inviteEmail}
                size="small"
                onChange={(e) => setInviteEmail(e.target.value)}
                error={!!inviteEmailError}
                helperText={
                  inviteEmailError || 'Type in the email of the athlete whose equipment you would like to manage.'
                }
                sx={{ flex: 1 }}
              />
              <Button
                variant="contained"
                startIcon={<PersonAddIcon />}
                onClick={handleInviteSubmit}
                disabled={inviteLoading}
                sx={{ mt: 0.5 }}
              >
                Send Request
              </Button>
            </Box>
          </Box>

          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Pending Requests
            </Typography>
            {renderPendingInvitations()}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default RequestAthleteManagementDialog;
